/**
 * ═══════════════════════════════════════════════════════════════
 * SAT Generator 3.0 — KaTeXRenderer Component
 *
 * PR-D4-020: LaTeX String Normalization, Sanitization & Parser Fix.
 *   - Fixes JSON double-escaping issues (\\\\ → \\).
 *   - Fixes corrupted ASCII control characters (\f → \frac, \t → \text/\times, \b → \beta, etc.).
 *   - Supports $$...$$, $...$, \[...\], \(...\) delimiters seamlessly.
 *   - Safe error handling: throwOnError: false, errorColor: '#ef4444'.
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useMemo } from 'react';
import katex from 'katex';

interface KaTeXRendererProps {
    /** Исходный текст с $...$, $$...$$, \(...\), \[...\] формулами */
    text: string | null | undefined;
    /** CSS-класс для контейнера */
    className?: string;
    /** Принудительно показывать как block (true) или inline (false) */
    displayMode?: boolean;
}

/**
 * Санитизирует и нормализует сырые LaTeX строки из JSON / API бэкенда.
 * Устраняет повреждения экранирования и ASCII control-characters.
 */
export function sanitizeLatex(input: string): string {
    if (!input || typeof input !== 'string') return '';

    let s = input;

    // 1. Восстановление повреждённых escape-последовательностей от JSON parser:
    // \f (form feed, \x0c) -> \frac, \forall, etc.
    s = s.replace(/\x0c(?:f?)(rac|orall)/g, (_, m: string) => '\\' + 'f' + m);
    s = s.replace(/\x0c([a-zA-Z])/g, (_, m: string) => '\\' + 'f' + m);

    // \t (tab, \x09) -> \text, \times, \theta, \tan, \tau, \triangle, etc.
    s = s.replace(/\t(?:t?)(ext|imes|heta|an|au|riangle|binom)/g, (_, m: string) => '\\' + 't' + m);

    // \b (backspace, \x08) -> \beta, \bar, \begin, \binom, \bullet, etc.
    s = s.replace(/\x08(?:b?)(eta|ar|egin|inom|ullet|matrix|pm)/g, (_, m: string) => '\\' + 'b' + m);

    // \v (vertical tab, \x0b) -> \vec, \varphi, \vspace, etc.
    s = s.replace(/\x0b(?:v?)(ec|arphi|space|dots)/g, (_, m: string) => '\\' + 'v' + m);

    // 2. Устранение чрезмерного экранирования бэкслешей в формулах
    // Например: \\\\frac{...} -> \\frac{...}, \\\\theta -> \\theta
    s = s.replace(/\\\\([a-zA-Z]+)/g, (_, m: string) => '\\' + m);

    // 3. Унификация блочных скобок \[ ... \] в $$ ... $$
    s = s.replace(/\\\[([\s\S]*?)\\\]/g, (_, m: string) => `$$${m}$$`);

    // 4. Унификация инлайн скобок \( ... \) в $ ... $
    s = s.replace(/\\\(([\s\S]*?)\\\)/g, (_, m: string) => `$${m}$`);

    return s;
}

interface Segment {
    type: 'text' | 'latex';
    content: string;
    display: boolean;
}

/**
 * Разделяет текст на сегменты: plain text и LaTeX формулы.
 * Корректно распознаёт $$display$$ и $inline$ математику.
 */
export function splitLatex(rawText: string): Segment[] {
    const text = sanitizeLatex(rawText);
    const segments: Segment[] = [];
    let remaining = text;

    while (remaining.length > 0) {
        // 1. Ищем $$...$$ (display mode)
        const displayMatch = remaining.match(/^\$\$([\s\S]+?)\$\$/);
        if (displayMatch) {
            const content = displayMatch[1].trim();
            if (content.length > 0) {
                segments.push({ type: 'latex', content, display: true });
            }
            remaining = remaining.slice(displayMatch[0].length);
            continue;
        }

        // 2. Ищем $...$ (inline mode) — непустой, не чисто пробельный
        const inlineMatch = remaining.match(/^\$([^\$]+?)\$/);
        if (inlineMatch) {
            const content = inlineMatch[1].trim();
            if (content.length > 0) {
                segments.push({ type: 'latex', content, display: false });
            }
            remaining = remaining.slice(inlineMatch[0].length);
            continue;
        }

        // 3. Ищем следующий символ $
        const nextDollar = remaining.indexOf('$');
        if (nextDollar === -1) {
            // Больше нет формул — весь остаток plain text
            segments.push({ type: 'text', content: remaining, display: false });
            break;
        }

        if (nextDollar > 0) {
            segments.push({ type: 'text', content: remaining.slice(0, nextDollar), display: false });
            remaining = remaining.slice(nextDollar);
        } else {
            // Текущий символ $, но он не закрывается или одиночный (например "$100 and no end")
            // Проверяем, есть ли дальше закрывающий $
            const nextClosing = remaining.slice(1).indexOf('$');
            if (nextClosing === -1) {
                segments.push({ type: 'text', content: remaining, display: false });
                break;
            } else {
                // Продвигаемся на 1 символ
                segments.push({ type: 'text', content: '$', display: false });
                remaining = remaining.slice(1);
            }
        }
    }

    return segments;
}

/**
 * Рендерит один LaTeX-фрагмент в HTML-строку через KaTeX.
 * Гарантирует безопасность от падений (throwOnError: false, errorColor: '#ef4444').
 */
export function renderLatex(latex: string, display: boolean): string {
    const cleanFormula = latex
        .replace(/\\\\/g, '\\') // Заменяем двойные экранирования внутри формулы
        .trim();

    try {
        return katex.renderToString(cleanFormula, {
            displayMode: display,
            throwOnError: false,
            errorColor: '#ef4444',
            strict: false,
            trust: true,
        });
    } catch {
        // Fallback: безопасное экранирование сырого текста
        return `<span class="text-red-500 font-mono text-xs">${escapeHtml(latex)}</span>`;
    }
}

function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function KaTeXRenderer({ text, className = '', displayMode }: KaTeXRendererProps) {
    const rendered = useMemo(() => {
        if (!text) return null;

        const segments = splitLatex(text);
        const elements: React.ReactNode[] = [];

        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            if (seg.type === 'text') {
                elements.push(
                    <React.Fragment key={i}>
                        {seg.content}
                    </React.Fragment>,
                );
            } else {
                const isDisplay = displayMode !== undefined ? displayMode : seg.display;
                const html = renderLatex(seg.content, isDisplay);
                elements.push(
                    <span
                        key={i}
                        dangerouslySetInnerHTML={{ __html: html }}
                        className={isDisplay ? 'katex-block my-2 block overflow-x-auto text-center' : 'katex-inline inline-baseline'}
                    />,
                );
            }
        }

        return elements;
    }, [text, displayMode]);

    if (!text) return null;

    return <span className={className}>{rendered}</span>;
}

/**
 * Утилита для рендеринга plain text → HTML с KaTeX.
 */
export function renderLatexToHtml(text: string): string {
    if (!text) return '';
    const segments = splitLatex(text);
    return segments
        .map((seg) => {
            if (seg.type === 'text') return escapeHtml(seg.content);
            return renderLatex(seg.content, seg.display);
        })
        .join('');
}

export default KaTeXRenderer;